import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import { Skeleton } from '@/components/ui/Skeleton'
import { moodLabel } from './describe'
import { RatingStars } from './RatingStars'
import styles from './RatingsPage.module.css'

interface RatingDistributionProps {
  /** Keyed "1".."5". Undefined while loading. */
  distribution: Record<string, number> | undefined
  isLoading: boolean
  /** The star count currently filtered on, as the filter string ("" for none). */
  selected: string
  onSelect: (stars: string) => void
}

/**
 * How many of each score, five stars at the top.
 *
 * A bar list rather than a pie: every row carries its exact count and its
 * share, and each is a way into the list filtered to that score. The two low
 * rows are tinted so a bulge at the bottom reads as a problem before the
 * numbers are read.
 */
export function RatingDistribution({ distribution, isLoading, selected, onSelect }: RatingDistributionProps) {
  if (isLoading && !distribution) {
    return (
      <div className={styles.distribution}>
        {[5, 4, 3, 2, 1].map((stars) => (
          <Skeleton key={stars} height={28} radius="var(--radius-md)" />
        ))}
      </div>
    )
  }

  const counts = [5, 4, 3, 2, 1].map((stars) => ({ stars, count: distribution?.[String(stars)] ?? 0 }))
  const total = counts.reduce((sum, entry) => sum + entry.count, 0)

  return (
    <ul className={styles.distribution} role="list">
      {counts.map(({ stars, count }) => {
        const share = total === 0 ? 0 : count / total
        const active = selected === String(stars)
        return (
          <li key={stars}>
            <button
              type="button"
              className={cn(styles.distributionRow, active && styles.distributionRowActive)}
              aria-pressed={active}
              aria-label={`${stars} ${stars === 1 ? 'star' : 'stars'}, ${moodLabel(stars)}: ${count} ${count === 1 ? 'rating' : 'ratings'}`}
              onClick={() => onSelect(String(stars))}
            >
              {/* Decorative: the button's own label already says the score. */}
              <span className={styles.distributionStars} aria-hidden="true">
                <RatingStars value={stars} size="sm" />
              </span>
              <span className={styles.distributionTrack}>
                <span
                  className={cn(styles.distributionFill, stars <= 2 && styles.distributionFillLow)}
                  style={{ width: `${Math.max(share * 100, count > 0 ? 2 : 0)}%` }}
                />
              </span>
              <span className={styles.distributionCount}>
                {formatNumber(count)}
                <span className={styles.distributionShare}>{total === 0 ? '' : `${Math.round(share * 100)}%`}</span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
